import { useState, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Shield, Plus, Trash2, Pencil, ArrowLeft, Check, AlertTriangle } from 'lucide-react';
import { isAdminSession, adminLogin } from '@/utils/auth';
import {
  getAllRiskRules,
  addCustomRiskRule,
  updateCustomRiskRule,
  deleteCustomRiskRule,
  type RiskRule,
  type RiskLevel,
} from '@/utils/riskRules';

const LEVEL_META: Record<RiskLevel, { label: string; color: string; bg: string }> = {
  high:   { label: '高风险', color: 'text-red-700', bg: 'bg-red-50' },
  medium: { label: '中风险', color: 'text-yellow-700', bg: 'bg-yellow-50' },
  low:    { label: '低风险', color: 'text-blue-700', bg: 'bg-blue-50' },
};

export default function RiskRuleManager() {
  const navigate = useNavigate();
  const isAdmin = isAdminSession();

  const [rules, setRules] = useState<RiskRule[]>(() => getAllRiskRules());
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  // 表单状态
  const [formLevel, setFormLevel] = useState<RiskLevel>('high');
  const [formKeyword, setFormKeyword] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formBasis, setFormBasis] = useState('');

  // 按等级分组
  const grouped = useMemo(() => {
    const g: Record<RiskLevel, RiskRule[]> = { high: [], medium: [], low: [] };
    for (const r of rules) (g[r.level]).push(r);
    return g;
  }, [rules]);

  // 刷新
  const refresh = useCallback(() => setRules(getAllRiskRules()), []);

  // 保存（新增 / 编辑）
  const handleSave = useCallback(() => {
    if (!formKeyword.trim() || !formDesc.trim()) {
      alert('请填写关键词和风险提示');
      return;
    }
    if (editingId) {
      updateCustomRiskRule(editingId, {
        level: formLevel,
        keyword: formKeyword.trim(),
        description: formDesc.trim(),
        legalBasis: formBasis.trim(),
      });
    } else {
      addCustomRiskRule({
        level: formLevel,
        keyword: formKeyword.trim(),
        description: formDesc.trim(),
        legalBasis: formBasis.trim(),
      });
    }
    resetForm();
    refresh();
  }, [editingId, formLevel, formKeyword, formDesc, formBasis, refresh]);

  const resetForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormLevel('high');
    setFormKeyword('');
    setFormDesc('');
    setFormBasis('');
  };

  const handleEdit = (rule: RiskRule) => {
    if (rule.isBuiltIn) {
      alert('内置规则不可编辑，您可以新建一条自定义规则替代。');
      return;
    }
    setEditingId(rule.id);
    setFormLevel(rule.level);
    setFormKeyword(rule.keyword);
    setFormDesc(rule.description);
    setFormBasis(rule.legalBasis || '');
    setShowForm(true);
  };

  const handleDelete = (rule: RiskRule) => {
    if (rule.isBuiltIn) {
      alert('内置规则不可删除。');
      return;
    }
    if (!window.confirm(`确定删除规则「${rule.keyword}」？`)) return;
    deleteCustomRiskRule(rule.id);
    refresh();
  };

  if (!isAdmin) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold text-gray-800">风险规则管理</h1>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">
          <AlertTriangle className="w-10 h-10 text-yellow-500 mx-auto mb-3" />
          <p className="text-yellow-700 font-medium">需要管理员权限才能管理风险规则</p>
          <button
            onClick={() => {
              const pwd = prompt('请输入管理员密码：');
              if (pwd === null) return;
              if (adminLogin(pwd)) {
                alert('管理员登录成功！');
                window.location.reload();
              } else {
                alert('密码错误！');
              }
            }}
            className="mt-4 inline-flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark"
          >
            管理员登录
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 顶部 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/risk-report" className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <Shield className="w-6 h-6 text-primary" />
          <h1 className="text-xl font-bold text-gray-800">风险规则管理</h1>
          <span className="text-sm text-gray-400">共 {rules.length} 条规则（{rules.filter(r => !r.isBuiltIn).length} 自定义）</span>
        </div>
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark"
        >
          <Plus className="w-4 h-4" />
          新增规则
        </button>
      </div>

      {/* 新增 / 编辑表单 */}
      {showForm && (
        <div className="bg-white rounded-xl border border-gray-100 p-6 space-y-4">
          <h3 className="font-bold text-gray-800">{editingId ? '编辑规则' : '新增自定义规则'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">风险等级</label>
              <select
                value={formLevel}
                onChange={e => setFormLevel(e.target.value as RiskLevel)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                <option value="high">高风险</option>
                <option value="medium">中风险</option>
                <option value="low">低风险</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">匹配关键词 *</label>
              <input
                type="text"
                value={formKeyword}
                onChange={e => setFormKeyword(e.target.value)}
                placeholder="如：口头约定"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">风险提示 *</label>
              <input
                type="text"
                value={formDesc}
                onChange={e => setFormDesc(e.target.value)}
                placeholder="如：未以书面形式确定合同内容，难以举证"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">法律依据（选填）</label>
              <input
                type="text"
                value={formBasis}
                onChange={e => setFormBasis(e.target.value)}
                placeholder="如：《民法典》第469条"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>
          </div>
          <div className="flex items-center justify-end gap-3 pt-2">
            <button onClick={resetForm} className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50">取消</button>
            <button onClick={handleSave} className="px-4 py-2 rounded-lg bg-primary text-white hover:bg-primary-dark flex items-center gap-2">
              <Check className="w-4 h-4" />
              {editingId ? '保存修改' : '确认新增'}
            </button>
          </div>
        </div>
      )}

      {/* 规则列表（按等级分组） */}
      {(['high', 'medium', 'low'] as RiskLevel[]).map(level => {
        const meta = LEVEL_META[level];
        const items = grouped[level];
        if (items.length === 0) return null;
        return (
          <div key={level} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className={`px-6 py-3 font-medium ${meta.bg} ${meta.color} text-sm`}>
              {meta.label}（{items.length} 条）
            </div>
            <div className="divide-y divide-gray-100">
              {items.map(rule => (
                <div key={rule.id} className="px-6 py-3 flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-800">{rule.keyword}</span>
                      {rule.isBuiltIn && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">内置</span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600 mt-0.5">{rule.description}</div>
                    {rule.legalBasis && (
                      <div className="text-xs text-gray-400 mt-0.5">依据：{rule.legalBasis}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {!rule.isBuiltIn && (
                      <>
                        <button onClick={() => handleEdit(rule)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500" title="编辑">
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDelete(rule)} className="p-1.5 rounded-lg hover:bg-red-50 text-gray-500 hover:text-red-600" title="删除">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                    {rule.isBuiltIn && (
                      <span className="text-xs text-gray-400" title="内置规则不可修改">🔒</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* 说明 */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-700">
        💡 <strong>使用说明：</strong>风险扫描时，系统会同时匹配内置规则和您新增的自定义规则。匹配到关键词后，会在法审报告中提示对应风险等级和建议。
      </div>
    </div>
  );
}
